import sortBy from "lodash/sortBy";

import { CategoryVariant, CE_05, CE_12, CUSTOM, ViewType } from "@helpers/plasmic/slices";

import {
  CATEGORY_HOME_LINK_TYPE,
  CATEGORY_PRODUCT_LISTING_LINK_TYPE,
  FILTER_OPTIONS_VALUES,
  FilterOptionValue,
} from "@stadium/shared/src/constants/stores/routes";
import { ALL_PRODUCTS, ALL_SUBCATEGORY_CODE } from "@constants/routes";
import { getCategoriesByCategorySlug } from "@helpers/plasmic/store";

export const sortByCustomOrdering = (categories: any[], orderCategories: string[]) => {
  return sortBy(categories, (category) => {
    const index = orderCategories?.indexOf(category.code);
    return index === -1 ? Infinity : index;
  });
};

export const getCategoriesToRender = ({
  viewType,
  variant,
  showSubcategories,
  categorySlug,
  topLevelCategories,
  customCategories,
  hideCategories,
  visibleCategoryCount,
  orderCategories,
  allCategoriesData,
  parentCategoriesData,
  skipTitleHumanization,
}: {
  viewType: ViewType;
  variant: CategoryVariant;
  showSubcategories: boolean;
  categorySlug?: string;
  topLevelCategories?: any[];
  customCategories?: any[];
  hideCategories: string[];
  visibleCategoryCount?: number;
  orderCategories: string[];
  allCategoriesData: any[];
  parentCategoriesData: any[];
  skipTitleHumanization: boolean;
}) => {
  const hasAnyCategorySlug =
    categorySlug && categorySlug !== ALL_PRODUCTS && categorySlug !== ALL_SUBCATEGORY_CODE;

  const { parentCategories, allCategories } = hasAnyCategorySlug
    ? getCategoriesByCategorySlug({
        variant,
        categorySlug,
        allCategoriesData,
        skipTitleHumanization,
      })
    : { parentCategories: parentCategoriesData, allCategories: allCategoriesData };

  const isCustomCategories = viewType === CUSTOM && variant !== CE_05;
  const sliceCategories =
    showSubcategories || isCustomCategories ? allCategories : parentCategories;

  if (isCustomCategories) {
    if (variant !== CE_12) {
      const validCategoryCodes = new Set(allCategories.map((cat) => cat.code));
      const categoriesToRender = (customCategories || []).filter(
        (customCategory) => customCategory?.code && validCategoryCodes.has(customCategory.code),
      );
      return { categoriesToRender, sliceCategories, isCustomCategories, parentCategories };
    }

    const allCategoriesMap = new Map(allCategories.map((category) => [category.code, category]));

    const categoriesToRender = (topLevelCategories || [])
      .map((customCategory) => {
        const baseCategory = allCategoriesMap.get(customCategory.code);
        if (!baseCategory) return null;

        const baseCategoryChildrenSet = new Set(
          ((baseCategory as any).children || []).map((child: any) => child.code),
        );

        const childCategories = (customCategories || [])
          .filter((childCustomCategory) => baseCategoryChildrenSet.has(childCustomCategory.code))
          .map((childCustomCategory) => {
            const baseChild = allCategoriesMap.get(childCustomCategory.code);
            return baseChild ? { ...baseChild, ...childCustomCategory } : null;
          })
          .filter(Boolean);

        return {
          ...baseCategory,
          ...customCategory,
          children: childCategories,
        };
      })
      .filter(Boolean);

    return { categoriesToRender, sliceCategories, isCustomCategories, parentCategories };
  }

  const hideCategoriesSet = new Set(hideCategories);
  let filteredCategories = (parentCategories || []).filter(
    (category) => !hideCategoriesSet.has(category.code),
  );

  if (visibleCategoryCount && visibleCategoryCount > 0) {
    filteredCategories = filteredCategories.slice(0, visibleCategoryCount);
  }

  const categoriesToRender = sortByCustomOrdering(filteredCategories, orderCategories);
  return { categoriesToRender, sliceCategories, isCustomCategories, parentCategories };
};

export const getDefaultUrlType = (category?: any) => {
  return category ? CATEGORY_PRODUCT_LISTING_LINK_TYPE : CATEGORY_HOME_LINK_TYPE;
};

export const getCategoryLinkQueryFromFilterOptions = (
  filterOptions?: string,
): Record<string, string> => {
  if (!filterOptions) return {};
  const query = FILTER_OPTIONS_VALUES[filterOptions as FilterOptionValue];
  return query ? { ...query } : {};
};
